import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Energie } from 'src/app/core/_models/energie';
import { EnvironmentalData } from 'src/app/core/_models/environmentalData';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { EnvironmentalDataService } from 'src/app/core/_services/environmentalDataService';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-energie',
  templateUrl: './energie.component.html',
  styleUrls: ['./energie.component.scss']
})
export class EnergieComponent implements OnInit {
  unSaved: boolean = false;

  perimeterId!: string;
  currentItem: Energie = {};
  form!: FormGroup;
  favoriteId?: string;
  manageEnergies!: boolean;
  energyType: 'vehicle' | 'transportation' | null = null;

  versionId: string = '';
  dbVersions: Version[] = [];
  coefficientCO2?: number;
  coefficientMJ?: number;
  environmentalData?: EnvironmentalData;

  fileToUpload?: File;
  justificationFilePath: string;

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;

  constructor(
    private activeRoute: ActivatedRoute,
    private store: Store<AppState>,
    private router: Router,
    private snackbarService: SnackbarService,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private utilsService: UtilsService,
    private energieService: EnergieService,
    private versionService: VersionService,
    private environmentalDataService: EnvironmentalDataService,
    private authService: AuthService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService,
    private uploadService: UploadService,
    private spinnerService: SpinnerOverlayService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';
  }

  ngOnInit(): void {
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.energyType = this.activeRoute.snapshot.data['energyType'] || null;

    this.activeRoute.params.subscribe((params) => {
      const resourceId = params['id'];
      const isNew = !resourceId || resourceId === '0' || this.activeRoute.snapshot.url.some((s) => s.path === 'new');

      if (!isNew) {
        this.versionService.getAll().subscribe((versions) => {
          this.dbVersions = versions;

          this.form.patchValue({
            versionId: versions[0].versionId
          });
          if (versions[0]) {
            this.versionId = versions[0].versionId!;
          }

          this.getById(resourceId).subscribe(
            (energie) => {
              const vId = energie.ruade ? this.versionId : undefined;

              this.getEnvData(resourceId, vId).subscribe((environmentalData: EnvironmentalData) => {
                energie.environmentalData = environmentalData;
                this.coefficientCO2 = environmentalData.coefficientCO2;
                this.coefficientMJ = environmentalData.coefficientMJ;

                this.form.patchValue(energie);
                if (this.isVehicle) {
                  this.form.patchValue({
                    environmentalFactor: environmentalData.environmentalFactor
                  });
                } else {
                  this.form.patchValue({
                    coefficientCO2: environmentalData.coefficientCO2,
                    coefficientMJ: environmentalData.coefficientMJ
                  });
                }
                if (energie.ruade) {
                  this.checkVersion(environmentalData);
                }
                this.currentItem = energie;
                if (this.currentItem.resourceId) {
                  this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
                    this.favoriteId = res ? res.userFavoriteId : '';
                  });
                }
                this.unSaved = false;
              });
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
              this.router.navigate([SiteMapUrl.MenuEnergie]);
            }
          );
        });
      }

      this.form = this.formBuilder?.group({
        resourceId: [''],
        status: [],
        familyId: [],
        description: [],
        code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(9)]],
        displayName: ['', Validators.required],
        coefficientCO2: ['', this.isVehicle ? [] : Validators.required],
        coefficientMJ: ['', this.isVehicle ? [] : Validators.required],
        environmentalFactor: ['', this.isVehicle ? Validators.required : []],
        versionId: [],
        fileName: [],
        fileDescription: []
      });
    });
  }

  ngDoCheck(): void {
    if (!this.authService?.accessesValues) return;
    const code = this.isVehicle ? FunctionalityCode.ManageVehicleEnergie : FunctionalityCode.ManageEnergie;
    this.manageEnergies = this.authService.accessesValues.includes(code);
  }

  get formCrls() {
    return this.form?.controls;
  }

  onVersionChange(value: string) {
    this.versionId = value;
    const vId = this.currentItem.ruade ? value : undefined;
    this.getEnvData(this.currentItem.resourceId!, vId).subscribe((environmentalData: EnvironmentalData) => {
      this.currentItem.environmentalData = environmentalData;
      this.form.patchValue(this.currentItem);
      this.coefficientCO2 = environmentalData.coefficientCO2;
      this.coefficientMJ = environmentalData.coefficientMJ;

      if (this.isVehicle) {
        this.form.patchValue({
          environmentalFactor: environmentalData.environmentalFactor
        });
      } else {
        this.form.patchValue({
          coefficientCO2: environmentalData.coefficientCO2,
          coefficientMJ: environmentalData.coefficientMJ
        });
      }

      this.checkVersion(environmentalData);
    });
  }

  checkVersion(environmentalData: EnvironmentalData) {
    if (environmentalData.environmentalDataId === GlobalConstants.EMPTY_GUID) {
      this.snackbarService.showMessage(this.translate.instant('error_resource_not_in_version'));
    }
  }

  async clickSave(energie: Energie) {
    if (this.form.invalid) {
      return;
    }

    this.unSaved = false;

    const co2 = this.toNullableNumber(energie.coefficientCO2);
    const mj = this.toNullableNumber(energie.coefficientMJ);
    const ef = this.toNullableNumber(energie.environmentalFactor);

    const envData: any = this.isVehicle ? { environmentalFactor: ef } : { coefficientCO2: co2, coefficientMJ: mj };
    if (this.currentItem?.environmentalData?.resourceId) envData.resourceId = this.currentItem.environmentalData.resourceId;
    if (this.currentItem?.environmentalData?.environmentalDataId) envData.environmentalDataId = this.currentItem.environmentalData.environmentalDataId;
    if (this.currentItem?.ruade && this.currentItem?.environmentalData?.versionId) envData.versionId = this.currentItem.environmentalData.versionId;

    const payload: any = {
      ...energie,
      environmentalData: envData
    };
    if (this.isVehicle) {
      payload.environmentalFactor = ef;
      payload.coefficientCO2 = null;
      payload.coefficientMJ = null;
    } else {
      payload.coefficientCO2 = co2;
      payload.coefficientMJ = mj;
      payload.environmentalFactor = undefined;
    }

    if (!this.currentItem?.ruade) delete payload.versionId;

    if (!this.formCrls?.resourceId?.value) {
      delete payload.resourceId;
      this.create(payload).subscribe(
        (data: Energie) => {
          this.currentItem = data;
          this.currentItem.coefficientCO2 = this.coefficientCO2?.toString();
          this.currentItem.coefficientMJ = this.coefficientMJ?.toString();
          this.majStore(SnackbarTypes.add);
          history.state.resourceId = data.resourceId;
          this.router.navigate([`${SiteMapUrl.MenuEnergie}/${this.energyType}/${data.resourceId}`], { state: history.state });
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    } else {
      this.update(payload.resourceId!, payload).subscribe(
        (data: Energie) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.update);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }
  }

  private toNullableNumber(value: any): any {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return isNaN(n) ? null : n;
  }

  majStore(snackbarMsgType: string) {
    this.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  clickDuplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 9, duplicate: (code: string) => this.duplicateEnergy(resourceId, code) } })
      .afterClosed()
      .subscribe((data: Energie) => {
        if (data === undefined) return;
        this.majStore(SnackbarTypes.duplicate);
        this.router.navigate([`${SiteMapUrl.MenuEnergie}/${this.energyType}/${data.resourceId}`], { state: history.state });
      });
  }

  openDialogDelete(resourceId?: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_energie') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.delete(resourceId);
      }
    });
  }

  delete(resourceId?: string) {
    if (!resourceId) return;

    this.deleteEnergy(resourceId).subscribe({
      next: () => {
        this.majStore(SnackbarTypes.delete);
        this.router.navigate([SiteMapUrl.MenuEnergie]);
      },
      error: (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        console.error(error);
      }
    });
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId) return;

    if (files && files.length > 0) {
      this.fileToUpload = <File>files[0];
    }

    if (!this.fileToUpload) return;

    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload?.name?.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          if (this.fileInput) this.fileInput.nativeElement.value = '';
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.setValue(null);
    this.form.get('fileDescription')?.setValue(null);
    this.fileToUpload = undefined;
    if (this.currentItem?.resourceId) {
      this.clickSave(this.form.value);
    }
  }

  private get isVehicle(): boolean {
    return this.energyType === 'vehicle';
  }

  private getById(id: string) {
    return this.isVehicle ? this.energieService.getForVehicle(id) : this.energieService.getForTransportation(id);
  }

  private getEnvData(id: string, versionId?: string) {
    return this.isVehicle ? this.environmentalDataService.getVehicleEnvironmentalData(id, versionId) : this.environmentalDataService.getTransportationFuelEnvironmentalData(id, versionId);
  }

  private create(energie: Energie) {
    return this.isVehicle ? this.energieService.createForVehicle(energie) : this.energieService.createForTransportation(energie);
  }

  private update(id: string, energie: Energie) {
    return this.isVehicle ? this.energieService.updateForVehicle(id, energie) : this.energieService.updateForTransportation(id, energie);
  }

  private deleteEnergy(id: string) {
    return this.isVehicle ? this.energieService.deleteForVehicle(id) : this.energieService.deleteForTransportation(id);
  }

  private duplicateEnergy(id: string, code: string) {
    return this.isVehicle ? this.energieService.duplicateForVehicle(id, code) : this.energieService.duplicateForTransportation(id, code);
  }

  private getAll(groupBy?: string) {
    return this.isVehicle ? this.energieService.getAllForVehicle(groupBy) : this.energieService.getAllForTransportation(groupBy);
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }
}
